import * as THREE from 'three';

import { tryFloorConvexHull } from '../../../facets/floor-footprint';
import { createLabelMeasurementContext } from '../../../../gui/Label3D';
import { getLevelElevationByName } from './LevelUtils';
import { LABEL_ANCHORING } from '../../HudLabel';
import { FacetTypes } from "../../../facets/Facets";

const _box2 = new THREE.Box2();
const _vec2 = new THREE.Vector2();

const IDLE_FRAME_BUDGET = 2; // If under this ms, don't wait for next idle frame.
const LABEL_HEIGHT_PX = 22; // matches label component height (incl padding) but minus a bit
const LEVEL_LABEL_FONT = '300 16px ArtifaktElement'; // match css
const MAX_LVL_MESH_TRIES = 3; // Number of times to replace is idle load queue if incomplete.

export class LevelLabelPlacer {
  boxes = [];
  layer;

  /** Hull states, includes details about completion, settling, hull, and minimum Z. */
  hulls = new Map();
  loadQueue = [];
  idsInQueue = new Set();

  constructor(levelsLayer, onAllLoaded) {
    this.layer = levelsLayer;
    this.onAllLoaded = onAllLoaded;

    this.measureCtx = createLabelMeasurementContext(levelsLayer.viewer);
    this.measureCtx.font = LEVEL_LABEL_FONT;
  }

  addToIdleCacheQueue(facility, level) {
    if (this.idsInQueue.has(level.id)) return;

    this.idsInQueue.add(level.id);
    const length = this.loadQueue.push({ id: level.id, tries: 0 });
    if (length === 1) {
      this.#processQueueItem(facility);
    }
  }

  getBoundingBox(text, pos3D, anchor, box2) {
    const textWidthPx = this.measureCtx.measureText(text).width;
    const { x, y } = this.layer.viewer.impl.worldToClient(pos3D);

    box2.makeEmpty();

    // Point close to the building.
    _vec2.x = x;
    _vec2.y = y + LABEL_HEIGHT_PX * 0.5;
    box2.expandByPoint(_vec2);

    // Point away from the building.
    const deltaX = anchor === LABEL_ANCHORING.START ? textWidthPx : anchor === LABEL_ANCHORING.END ? -textWidthPx : 0;
    _vec2.x += deltaX;
    _vec2.y -= LABEL_HEIGHT_PX;
    box2.expandByPoint(_vec2);

    return box2;
  }

  invalidateHulls() {
    this.hulls.clear();
    this.loadQueue.length = 0;
    this.idsInQueue.clear();
  }

  tryToPlace(placement, text) {
    if (!placement) return [];

    const { left } = placement;

    // Only care about left of the building for now.
    const pos3D = left;
    const anchor = LABEL_ANCHORING.END;
    const box2 = this.getBoundingBox(text, pos3D, anchor, _box2);

    // Overlap with previously set label.
    for (const existing of this.boxes) {
      if (existing.isIntersectionBox(box2)) {
        // Found a collision
        return [];
      }
    }

    this.boxes.push(box2.clone());
    return [pos3D, LABEL_ANCHORING.END];
  }

  /** Reset as part of an update before a series of "tryToPlace" */
  reset() {
    this.boxes.length = 0;
  }

  async #processQueueItem(facility, budget = 0) {

    if (budget <= 0) {
      // Out of allowance, wait for next idle frame.
      await Promise.all([
      facility.viewer.impl.waitForLoaderIdle(),
      facility.viewer.impl.waitForRendererIdle(),
      new Promise((resolve) => requestAnimationFrame(resolve))]
      );
      budget = IDLE_FRAME_BUDGET;
    }

    const item = this.loadQueue.shift();
    const levelsFacets = facility.facetsManager.getFacet(FacetTypes.levels);
    const level = levelsFacets.find((mn) => mn.id === item.id);

    let delta = 0;

    if (level) {
      const s0 = performance.now();
      const priorState = this.hulls.get(item.id) ?? {};
      const updatedState = tryFloorConvexHull(facility, level, priorState);
      this.hulls.set(item.id, updatedState);

      // Not finished, maybe still loading.
      //TODO: Not sure that if it fails once it will succeed later -- waiting for geometry loaded events
      //should be deterministic given we only react to model added events from the facets manager
      if (!updatedState.isComplete && item.tries < MAX_LVL_MESH_TRIES) {
        item.tries += 1;
        this.loadQueue.push(item);
      } else {
        updatedState.settled = true;
        this.idsInQueue.delete(item.id);
      }

      delta = performance.now() - s0;
    } else {
      //Case where the model for this filter item is currently off, so the facet doesn't exist at runtime (yet)
      this.idsInQueue.delete(item.id);
    }

    // console.info('Convex hull for level ' + level.id + ' took ' + delta + 'ms');

    // Launch next.
    if (this.loadQueue.length) {
      this.#processQueueItem(facility, budget - delta);
    } else {
      // Readjust all labels once the queue has been processed.
      // console.info('All level cache processed');
      this.onAllLoaded?.();
    }
  }

  guessPlacement(facility, levels, useCacheOnly) {
    const placementById = new Map();
    const elevByName = getLevelElevationByName(facility);

    //const s0 = performance.now();
    for (let i = 0; i < levels.length; i++) {
      const level = levels[i];

      const state = this.hulls.get(level.id);
      if (state?.hull) {
        // Calculate the best placement from a loaded floor highlight cache mesh.
        const placement = guessPlacementByFloorHull(facility, level, elevByName[level.id] ?? state.minZ, state.hull);
        if (placement) placementById.set(level.id, placement);
        continue;
      }

      if (!useCacheOnly) {
        // Register level cache to be loaded.
        this.addToIdleCacheQueue(facility, level);
      }
    }
    //console.log('guessPlacement in ', performance.now() - s0, 'ms for ', levels.length, 'levels');

    return placementById;
  }
}

/** Finds the left and right most point of the floor's convex hull based on screen coordinates. */
function guessPlacementByFloorHull(facility, level, elevation, hull) {

  // Some level have geometry get the necessary data in place
  const viewerImpl = facility.viewer.impl;
  let minX = Infinity,minVal = new THREE.Vector3();
  let maxX = -Infinity,maxVal = new THREE.Vector3();

  const p = new THREE.Vector3(0, 0, elevation);
  for (const pt of hull) {
    p.x = pt.x;
    p.y = pt.y;
    const { x } = viewerImpl.worldToClient(p);

    // Find left and right
    if (x < minX) {
      minX = x;
      minVal.copy(p);
    }
    if (x > maxX) {
      maxX = x;
      maxVal.copy(p);
    }
  }

  // Provide both left & right-most vertex to let the caller choose.
  const left = new THREE.Vector3(minVal.x, minVal.y, elevation);
  const right = new THREE.Vector3(maxVal.x, maxVal.y, elevation);
  return { left, right };
}